// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import cx from 'classnames'
import get from 'lodash/get'
import React from 'react'
import { Query } from 'react-apollo'
import Loader from '../../../common/loader/Loader'
import { alphaSort } from '../../../common/utils'
import { RestrictedConsumer } from '../../auth/RestrictedContext'
import type { User } from '../../auth/types'
import withUser from '../../auth/withUser'
import { i18n } from '../../index/index'
import ImageOverlay from '../ImageOverlay/ImageOverlay'
import RequiresAction from '../RequiresAction'
import WeeklyInfoPage from '../WeeklyInfoPage'
import Images from '../components/Images'
import Links from '../components/Links'
import css from '../weeklyInfoPage.css'
import { fetchWeeklyInfo } from '../weeklyInfoQueries'
import type { WeeklyInfoMarket, WeeklyInfoVariables } from '../weeklyInfoTypes'
import MarketSalesAndGrossProfit from './MarketSalesAndGrossProfit'
import MarketSalesAndHours from './MarketSalesAndHours'
import MarketWastages from './MarketWastages'
type MarketQuestionRowProps = {
  stationId: string
  date: string
  data: WeeklyInfoMarket
  canEdit: boolean
}

const MarketQuestionRow = ({ stationId, date, data, canEdit }: MarketQuestionRowProps) => (
  <TableRow>
    <TableCell className={cx(css.tableCell)}>{data.description}</TableCell>
    <TableCell className={cx(css.tableCell, css.actionCell)}>
      {canEdit && (
        <RequiresAction
          stationId={stationId}
          date={date}
          weeklyInfoId={data.id}
          subject='Market'
          isActionRequired={data.isActionRequired}
        />
      )}
    </TableCell>
  </TableRow>
)

type MarketQuestionProps = {
  stationId: string
  date: string
}

const MarketQuestion = ({ stationId, date }: MarketQuestionProps) => (
  <Query<Array<WeeklyInfoMarket>, WeeklyInfoVariables>
    query={fetchWeeklyInfo}
    variables={{
      stationId,
      date,
      type: 'Market',
    }}
  >
    {({ data, error, loading }) => (
      <Loader
        {...{
          error,
          loading,
        }}
      >
        <div className={css.marketQuestion}>
          <Typography variant='h6' gutterBottom>
            {i18n.t('weeklyInfo.title.marketQuestions')}
          </Typography>
          <RestrictedConsumer>
            {(hasPermission) => (
              <Paper>
                <Table className={css.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={css.tableHeading}>{i18n.t('weeklyInfo.heading.taskName')}</TableCell>
                      <TableCell className={cx(css.tableHeading, css.actionCell)}>
                        {hasPermission && i18n.t('weeklyInfo.heading.isActionRequired')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alphaSort('description', get(data, 'weeklyInfo', [])).map((item) => (
                      <MarketQuestionRow
                        stationId={stationId}
                        date={date}
                        data={item}
                        canEdit={hasPermission}
                        key={item.id}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </RestrictedConsumer>
        </div>
      </Loader>
    )}
  </Query>
)

type MarketPageProps = {
  user: User
}

@withUser
class MarketPage extends React.PureComponent<MarketPageProps> {
  constructor() {
    super()
    this.state = {
      imageOverlayIsOpen: false,
      selectedImage: null,
    }
  }

  selectImage = (imageContent: string) => {
    this.setState({
      selectedImage: imageContent,
      imageOverlayIsOpen: true,
    })
  }

  render() {
    const { user } = this.props
    const { imageOverlayIsOpen, selectedImage } = this.state
    return (
      <WeeklyInfoPage className={css.component} subject='Market'>
        {({ stationId, date, dateFormat, onDateChange, companyId }) => (
          <div className={css.content}>
            <ImageOverlay
              isOpen={imageOverlayIsOpen}
              image={selectedImage}
              onClose={() =>
                this.setState({
                  selectedImage: null,
                  imageOverlayIsOpen: false,
                })
              }
            />
            <MarketSalesAndHours date={date} stationId={stationId} />
            <MarketSalesAndGrossProfit date={date} stationId={stationId} />
            <MarketWastages date={date} stationId={stationId} />
            <MarketQuestion stationId={stationId} date={date} />

            <Images stationId={stationId} selectImage={this.selectImage} user={user} imageType={'weekinfo_market'} />

            <Links stationId={stationId} linkType={'weekinfo_market'} companyId={companyId} />
          </div>
        )}
      </WeeklyInfoPage>
    )
  }
}

export default MarketPage
